import api from '../../services/api';
import { ResponseData } from '../../types/response/ResponseData';
import ResponseError from '../../types/response/ResponseError';
import { Merchant } from '../../types/onboarding/Merchant';
import {
    RegisteredSignatory,
    SendAgreementRequest,
} from '../../types/onboarding/MerchantAgreement';
import { ONBOARDING_REQUEST_TIMEOUT_IN_MILLISECONDS } from './onboarding';

const merchantAgreementsPath = (organizationId: string, merchant: Merchant) =>
    `organizations/${organizationId}/merchants/${encodeURIComponent(
        merchant.countryCode
    )}/${encodeURIComponent(merchant.organizationNumber)}/agreements`;

/**
 * Lists who may sign for the merchant according to the register
 * (GET organizations/{id}/merchants/{countryCode}/{organizationNumber}/agreements/signatories). A merchant the
 * register has nothing on gets an empty list, and the agreement cannot be sent. Requires read on the
 * organization, like listing the merchants.
 */
export const listAgreementSignatories = async (
    organizationId: string,
    merchant: Merchant,
    listSignatoriesStarted: (merchant: Merchant) => void,
    listSignatoriesSucceeded: (merchant: Merchant, signatories: RegisteredSignatory[]) => void,
    listSignatoriesFailed: (error: ResponseError) => void
) => {
    listSignatoriesStarted(merchant);

    try {
        const response = await api.get<ResponseData<RegisteredSignatory[]>>(
            `${merchantAgreementsPath(organizationId, merchant)}/signatories`
        );

        listSignatoriesSucceeded(merchant, response.data ?? []);
    } catch (error) {
        listSignatoriesFailed(error as ResponseError);
    }
};

/**
 * Sends the agreement of the merchant for e-signing
 * (POST organizations/{id}/merchants/{countryCode}/{organizationNumber}/agreements): the agreement becomes a Scrive
 * document that is emailed to the signatories, and the merchant comes back with the agreement Pending. Not retried,
 * since a retry after a timeout would send a second document, and given the longer timeout because the PDF is
 * rendered and sent to Scrive in the request. Requires write on the organization.
 */
export const sendMerchantAgreement = async (
    organizationId: string,
    merchant: Merchant,
    request: SendAgreementRequest,
    sendAgreementStarted: () => void,
    sendAgreementSucceeded: (merchant: Merchant) => void,
    sendAgreementFailed: (error: ResponseError) => void
) => {
    sendAgreementStarted();

    try {
        const response = await api.postWithResponseType<
            SendAgreementRequest,
            ResponseData<Merchant>
        >(merchantAgreementsPath(organizationId, merchant), request, undefined, {
            timeout: ONBOARDING_REQUEST_TIMEOUT_IN_MILLISECONDS,
            retries: 0,
        });

        sendAgreementSucceeded(response.data);
    } catch (error) {
        sendAgreementFailed(error as ResponseError);
    }
};
